import { LanguageSupport, StreamLanguage } from '@codemirror/language';
import { styleTags, tags as t } from '@lezer/highlight';

/**
 * Custom BibTeX syntax highlighting mode for CodeMirror 6
 * Provides better highlighting than the generic stex mode
 */

// BibTeX entry types
const entryTypes = [
  'article', 'book', 'booklet', 'conference', 'inbook', 'incollection',
  'inproceedings', 'manual', 'mastersthesis', 'misc', 'phdthesis',
  'proceedings', 'techreport', 'unpublished', 'online', 'patent',
  'periodical', 'electronic', 'collection', 'dataset', 'software'
];

// Common BibTeX fields
const standardFields = [
  'author', 'title', 'journal', 'year', 'volume', 'number', 'pages',
  'month', 'note', 'key', 'publisher', 'editor', 'series', 'address',
  'edition', 'howpublished', 'booktitle', 'organization', 'chapter',
  'school', 'institution', 'type', 'doi', 'url', 'isbn', 'issn',
  'abstract', 'keywords', 'eprint', 'archiveprefix', 'primaryclass',
  'eid', 'numpages', 'acmid', 'articleno'
];

const bibtexMode = {
  name: 'bibtex',

  startState() {
    return {
      inEntry: false,
      inField: false,
      braceDepth: 0,
      expectingCiteKey: false
    };
  },

  token(stream, state) {
    // Skip whitespace
    if (stream.eatSpace()) {
      return null;
    }

    // Comments (% at start of line or after whitespace)
    if (stream.match(/^%.*$/)) {
      return 'comment';
    }

    // Entry type: @article, @book, etc.
    if (stream.match(/^@[a-zA-Z]+/i)) {
      const entryType = stream.current().substring(1).toLowerCase();
      state.inEntry = true;
      state.expectingCiteKey = true;

      if (entryTypes.includes(entryType)) {
        return 'keyword';
      }
      return 'keyword invalid';
    }

    // Opening brace for entry
    if (state.inEntry && stream.eat('{')) {
      state.braceDepth++;
      return 'bracket';
    }

    // Closing brace for entry
    if (state.inEntry && stream.eat('}')) {
      state.braceDepth--;
      if (state.braceDepth === 0) {
        state.inEntry = false;
        state.expectingCiteKey = false;
      }
      return 'bracket';
    }

    // Citation key (after opening brace)
    if (state.expectingCiteKey && stream.match(/^[^\s,}]+/)) {
      state.expectingCiteKey = false;
      return 'atom';
    }

    // Comma separator
    if (state.inEntry && stream.eat(',')) {
      return 'punctuation';
    }

    // Field name
    if (state.inEntry && stream.match(/^[a-zA-Z0-9_-]+(?=\s*=)/)) {
      const field = stream.current().toLowerCase();
      state.inField = true;

      if (standardFields.includes(field)) {
        return 'propertyName';
      }
      return 'propertyName invalid';
    }

    // Equals sign
    if (state.inField && stream.match(/^\s*=\s*/)) {
      return 'operator';
    }

    // String values in braces
    if (state.inField && stream.eat('{')) {
      let depth = 1;
      let escaped = false;

      while (depth > 0 && !stream.eol()) {
        const ch = stream.next();

        if (escaped) {
          escaped = false;
          continue;
        }

        if (ch === '\\') {
          escaped = true;
        } else if (ch === '{') {
          depth++;
        } else if (ch === '}') {
          depth--;
        }
      }

      state.inField = false;
      return 'string';
    }

    // String values in quotes
    if (state.inField && stream.eat('"')) {
      let escaped = false;

      while (!stream.eol()) {
        const ch = stream.next();

        if (escaped) {
          escaped = false;
          continue;
        }

        if (ch === '\\') {
          escaped = true;
        } else if (ch === '"') {
          break;
        }
      }

      state.inField = false;
      return 'string';
    }

    // Numeric values
    if (state.inField && stream.match(/^\d+/)) {
      state.inField = false;
      return 'number';
    }

    // String concatenation operator (#)
    if (state.inEntry && stream.eat('#')) {
      return 'operator';
    }

    // String variable references
    if (state.inField && stream.match(/^[a-zA-Z][a-zA-Z0-9_-]*/)) {
      state.inField = false;
      return 'variableName';
    }

    // Fallback: consume single character
    stream.next();
    return null;
  },

  languageData: {
    commentTokens: { line: '%' }
  }
};

// Create the language support with the custom mode
export function bibtex() {
  return new LanguageSupport(StreamLanguage.define(bibtexMode));
}

export default bibtex;
